const CHAT_SESSION_STORAGE_KEY = 'aes-ai-chat-session';
const CHAT_DIAGNOSTIC_RESPONSES_STORAGE_KEY = 'aes-ai-chat-diagnostic-responses';
// Six recent user/assistant exchanges are enough for ordinary follow-ups while
// keeping final-synthesis context deliberately bounded.
const MODEL_HISTORY_MESSAGE_LIMIT = 12;

const normalizeUserId = (userId) => (userId === null || userId === undefined ? '' : String(userId));

const isConversationMessage = (message) =>
  message &&
  (message.role === 'user' || message.role === 'assistant') &&
  typeof message.content === 'string';

const clearSavedConversation = (storage) => {
  try {
    storage.removeItem(CHAT_SESSION_STORAGE_KEY);
  } catch {
    return;
  }
};

const clearSavedMessages = (storage) => {
  try {
    storage.removeItem(CHAT_SESSION_STORAGE_KEY);
    storage.removeItem(CHAT_DIAGNOSTIC_RESPONSES_STORAGE_KEY);
  } catch {
    return;
  }
};

const isDiagnosticResponse = (response) => response &&
  typeof response.correlationId === 'string' &&
  response.correlationId.trim() &&
  typeof response.question === 'string';

const recordDiagnosticResponse = (storage, userId, response) => {
  const ownerUserId = normalizeUserId(userId);
  if (!ownerUserId || !isDiagnosticResponse(response)) return;

  try {
    const saved = JSON.parse(storage.getItem(CHAT_DIAGNOSTIC_RESPONSES_STORAGE_KEY));
    const responses = saved?.ownerUserId === ownerUserId && Array.isArray(saved.responses)
      ? saved.responses.filter(isDiagnosticResponse)
      : [];
    const nextResponses = [
      ...responses.filter((item) => item.correlationId !== response.correlationId),
      {
        correlationId: response.correlationId,
        question: response.question,
        createdAt: response.createdAt,
      },
    ].slice(-20);

    storage.setItem(CHAT_DIAGNOSTIC_RESPONSES_STORAGE_KEY, JSON.stringify({ ownerUserId, responses: nextResponses }));
  } catch {
    return;
  }
};

const getSavedMessages = (storage, userId) => {
  const ownerUserId = normalizeUserId(userId);

  if (!ownerUserId) {
    return [];
  }

  try {
    const savedSession = JSON.parse(storage.getItem(CHAT_SESSION_STORAGE_KEY));

    // Legacy arrays have no owner and are deliberately discarded rather than
    // being attributed to whichever user happens to sign in next.
    if (
      !savedSession ||
      savedSession.ownerUserId !== ownerUserId ||
      !Array.isArray(savedSession.messages)
    ) {
      clearSavedConversation(storage);
      return [];
    }

    return savedSession.messages.filter(isConversationMessage);
  } catch {
    clearSavedConversation(storage);
    return [];
  }
};

const saveMessages = (storage, userId, messages) => {
  const ownerUserId = normalizeUserId(userId);

  if (!ownerUserId) {
    return;
  }

  try {
    const messagesToSave = messages.filter((message) => !message.transient && isConversationMessage(message));

    if (!messagesToSave.length) {
      clearSavedConversation(storage);
      return;
    }

    const serializedSession = JSON.stringify({ ownerUserId, messages: messagesToSave });

    if (storage.getItem(CHAT_SESSION_STORAGE_KEY) !== serializedSession) {
      storage.setItem(CHAT_SESSION_STORAGE_KEY, serializedSession);
    }
  } catch {
    return;
  }
};

const buildChatHistory = (messages) => {
  const conversation = messages.filter((message) => !message.transient && isConversationMessage(message));
  const recentMessages = conversation.slice(-MODEL_HISTORY_MESSAGE_LIMIT);
  const latestAssistantIndex = conversation.map((message) => message.role).lastIndexOf('assistant');
  const activeClarification = conversation[latestAssistantIndex];
  const clarificationIsOutsideWindow =
    activeClarification?.clarification?.continuationToken &&
    latestAssistantIndex < conversation.length - recentMessages.length;
  const boundedMessages = clarificationIsOutsideWindow
    ? [activeClarification, ...recentMessages.slice(-(MODEL_HISTORY_MESSAGE_LIMIT - 1))]
    : recentMessages;

  return boundedMessages.map((message) => ({
    role: message.role,
    content: message.content,
    ...(message.role === 'assistant' && message.clarification?.continuationToken
      ? { clarificationToken: message.clarification.continuationToken } : {}),
  }));
};

const getRecentDiagnosticResponses = (messages, limit = 20) => {
  const responses = [];

  messages.forEach((message, index) => {
    if (message.role !== 'assistant' || !message.correlationId) {
      return;
    }

    const question = messages.slice(0, index).reverse().find((candidate) => candidate.role === 'user');

    responses.push({
      correlationId: message.correlationId,
      question: question?.content || '[Question unavailable]',
      answer: message.content,
      createdAt: message.createdAt,
    });
  });

  return responses.slice(-limit).reverse();
};

const getSavedDiagnosticResponses = (storage, userId, messages = [], limit = 20) => {
  const ownerUserId = normalizeUserId(userId);
  const fromConversation = getRecentDiagnosticResponses(messages, limit);
  if (!ownerUserId) return fromConversation;

  try {
    const saved = JSON.parse(storage.getItem(CHAT_DIAGNOSTIC_RESPONSES_STORAGE_KEY));
    const fromIndex = saved?.ownerUserId === ownerUserId && Array.isArray(saved.responses)
      ? saved.responses.filter(isDiagnosticResponse)
      : [];
    const merged = [...fromConversation, ...fromIndex].reduce((byCorrelationId, response) => {
      const existing = byCorrelationId.get(response.correlationId) || {};
      byCorrelationId.set(response.correlationId, { ...existing, ...response });
      return byCorrelationId;
    }, new Map());

    return Array.from(merged.values())
      .sort((left, right) => String(right.createdAt || '').localeCompare(String(left.createdAt || '')))
      .slice(0, limit);
  } catch {
    return fromConversation;
  }
};

const canSendChatMessage = (input, isBusy) => !isBusy && Boolean(input && input.trim());

const shouldSendChatMessageOnKeyDown = (event, isBusy) =>
  event?.key === 'Enter' && event.shiftKey !== true && event.nativeEvent?.isComposing !== true && !isBusy;

export {
  CHAT_SESSION_STORAGE_KEY,
  CHAT_DIAGNOSTIC_RESPONSES_STORAGE_KEY,
  MODEL_HISTORY_MESSAGE_LIMIT,
  buildChatHistory,
  canSendChatMessage,
  clearSavedMessages,
  getSavedMessages,
  getRecentDiagnosticResponses,
  getSavedDiagnosticResponses,
  recordDiagnosticResponse,
  saveMessages,
  shouldSendChatMessageOnKeyDown,
};
